import { entityId, formatDateTime, PAYMENT_METHOD_LABELS } from "./erp.js";

export const CASH_WORKSHEET_KIND_LABELS = Object.freeze({ opening: "Apertura", closing: "Cierre", in: "Ingreso", out: "Egreso", reversal: "Reversa", adjustment: "Ajuste" });
export const CASH_REVIEW_LABELS = Object.freeze({ approved: "Aprobado", observed: "Observado", pending: "Pendiente de revisión", not_required: "Sin revisión necesaria" });

function csvCell(value) {
  let text = String(value ?? "");
  // [WP-STABLE][CASH-EXPORT] Neutralizar fórmulas aportadas en nombres o notas;
  // los importes numéricos conservan su signo para permitir sumas en Excel.
  if (typeof value !== "number" && /^[\s\t\r\n]*[=+@-]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

function visibleAmount(value) {
  return value === undefined || value === null ? "Oculto" : value;
}

export function buildCashWorksheetCsv(data, { complete = false, page = 1 } = {}) {
  const session = data.cashSession || {};
  const totals = data.totals || {};
  const sales = data.salesSummary;
  const reviewHistory = session.reviewHistory || [];
  const table = [
    ["Planilla de caja", session.sessionNumber || ""],
    ["Alcance del archivo", complete ? "Turno completo" : `Sólo página ${page}`],
    ["Caja", data.register?.name || ""],
    ["Responsable", session.openedBy?.name || ""],
    ["Moneda", session.currency || data.register?.currency || "ARS"],
    ["Apertura", formatDateTime(session.openedAt)],
    ["Cierre", session.closedAt ? formatDateTime(session.closedAt) : "Turno abierto"],
    ["Cerrado por", session.closedBy?.name || ""],
    ["Apertura declarada", visibleAmount(session.openingAmount)],
    ["Ingresos del turno", totals.hidden ? "Oculto" : visibleAmount(totals.incoming)],
    ["Egresos del turno", totals.hidden ? "Oculto" : visibleAmount(totals.outgoing)],
    ["Saldo esperado del turno", totals.hidden ? "Oculto" : visibleAmount(totals.expected)],
    ["Declarado al cierre", session.status === "closed" ? visibleAmount(session.declaredClosingAmount) : "Pendiente"],
    ["Diferencia", session.status === "closed" ? visibleAmount(session.difference) : "Pendiente"],
    ...(sales && !totals.hidden ? [
      ["Total vendido cobrado en esta caja", visibleAmount(sales.salesAmount)],
      ["Ventas antes de anulaciones", visibleAmount(sales.salesReceipts)],
      ["Anulaciones de ventas descontadas en el turno", visibleAmount(sales.salesRefunds)],
      ["Impuestos de estas ventas", visibleAmount(sales.taxAmount)],
      ["Ventas sin impuestos", visibleAmount(sales.netSales)],
      ["Costo histórico de artículos vendidos", sales.costsHidden ? "Oculto" : sales.costOfSales == null ? "No disponible" : sales.costOfSales],
      ["Ganancia bruta estimada", sales.costsHidden ? "Oculto" : sales.grossProfit == null ? "No disponible" : sales.grossProfit],
      ["Criterio de ganancia", "Ventas sin impuestos menos costo histórico, en proporción a lo cobrado en esta caja; excluye apertura, antes de gastos y comisiones"],
      ...(!sales.costsHidden ? [["Líneas con costo registrado en cero", sales.zeroCostItemCount || 0], ["Líneas sin costo histórico válido", sales.missingCostItemCount || 0]] : []),
      ["Movimientos sin comprobante vinculado", sales.unlinkedMovementCount || 0],
    ] : []),
    ["Revisión", CASH_REVIEW_LABELS[session.reviewStatus] || "Sin revisión"],
    ["Notas de apertura", session.openingNotes || ""],
    ["Notas de cierre", session.closingNotes || ""],
    ["Notas de revisión", session.reviewNotes || ""],
    ["Filas exportadas", (data.cashMovements || []).length, "Filas del turno", data.total || 0],
    [],
    ["Movimiento", "Fecha", "Clase", "Concepto", "Medio", "Entrada", "Salida", "Saldo acumulado", "Estado", "Computa en saldo", "Comprobante ERP", "Referencia", "Registrado por"],
    ...(data.cashMovements || []).map((movement) => [
      movement.cashMovementNumber || "", formatDateTime(movement.occurredAt),
      CASH_WORKSHEET_KIND_LABELS[movement.kind] || movement.kind || "", movement.description || "",
      PAYMENT_METHOD_LABELS[movement.method] || movement.method || "",
      movement.direction === "in" ? movement.amount : "", movement.direction === "out" ? movement.amount : "",
      totals.hidden ? "Oculto" : visibleAmount(movement.runningBalance), movement.status || "",
      movement.affectsBalance === true ? "Sí" : "No", entityId(movement.erpMovementId || movement.movementId) || "",
      movement.reference || "", movement.createdByName || "",
    ]),
    [],
    ["Arqueo guardado", "Denominación", "Cantidad", "Subtotal"],
    ...Object.entries(session.denominationCounts || {}).filter(([, qty]) => Number(qty) > 0)
      .map(([bill, qty]) => ["Billetes", Number(bill), Number(qty), Number(bill) * Number(qty)]),
    ["Otros valores", "", "", session.otherCashAmount ?? ""],
    [],
    ["Historial de revisiones", "Fecha", "Responsable", "Estado", "Notas"],
    ...reviewHistory.map((review) => ["Revisión", formatDateTime(review.at), review.by?.name || "", CASH_REVIEW_LABELS[review.status] || review.status, review.notes || ""]),
  ];
  return `\ufeff${table.map((line) => line.map(csvCell).join(";")).join("\r\n")}`;
}

// [WP-STABLE][CASH-EXPORT] Reunir páginas sólo si todas pertenecen a la misma
// revisión. Cancelar descargas incompletas o mezcladas durante la operación.
export async function collectCashWorksheet(fetchPage, isCancelled = () => false) {
  const rows = [];
  const ids = new Set();
  let snapshot = null;
  const changed = () => new Error("El turno cambió mientras se preparaba el archivo. Volvé a exportarlo.");
  for (let number = 1; number <= 100; number += 1) {
    if (isCancelled()) return null;
    const chunk = await fetchPage(number, 200);
    if (isCancelled()) return null;
    if (!snapshot) snapshot = chunk;
    if (Number(chunk.total) > 20000) throw new Error("El turno supera 20.000 movimientos. Exportá por páginas para descargarlo completo.");
    if (!chunk.revision || chunk.revision !== snapshot.revision || chunk.total !== snapshot.total) throw changed();
    for (const row of chunk.cashMovements || []) {
      const id = String(entityId(row) || "");
      if (!id || ids.has(id)) throw changed();
      ids.add(id);
      rows.push(row);
    }
    if (!chunk.hasMore) {
      if (rows.length !== Number(snapshot.total)) throw changed();
      const final = await fetchPage(1, 200);
      if (isCancelled()) return null;
      if (final.revision !== snapshot.revision || final.total !== snapshot.total) throw changed();
      return { ...snapshot, cashMovements: rows };
    }
    if (!(chunk.cashMovements || []).length) throw changed();
  }
  throw new Error("El turno supera 20.000 movimientos. Exportá por páginas para descargarlo completo.");
}
